import * as React from 'react';
import { Progress } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { deleteClientAsync } from '../../../ajax/requests';
import { AnalysedGroup } from './types';

interface Props {
    groups: AnalysedGroup[];
    onClose: () => void;
}

type RowResult =
    | { status: 'deleted'; name: string }
    | { status: 'error'; name: string; message: string };

const ExecutionStep: React.FC<Props> = ({ groups, onClose }) => {
    const t = useTranslations();
    const cancelRef = React.useRef(false);

    const allToDelete = React.useMemo(
        () => groups.flatMap(g => g.toDelete),
        [groups],
    );

    const [processed, setProcessed] = React.useState(0);
    const [results, setResults] = React.useState<RowResult[]>([]);
    const [isDone, setIsDone] = React.useState(false);
    const [wasCancelled, setWasCancelled] = React.useState(false);

    const total = allToDelete.length;

    React.useEffect(() => {
        let isMounted = true;
        const accumulated: RowResult[] = [];

        async function run() {
            for (let i = 0; i < allToDelete.length; i++) {
                if (cancelRef.current) {
                    if (isMounted) setWasCancelled(true);
                    break;
                }

                const client = allToDelete[i];
                try {
                    await deleteClientAsync(client.id!);
                    accumulated.push({ status: 'deleted', name: client.name ?? client.id });
                } catch (err) {
                    const msg = err instanceof Error ? err.message : t(TK.somethingWentWrong);
                    accumulated.push({ status: 'error', name: client.name ?? client.id, message: msg });
                }

                if (isMounted) {
                    setProcessed(i + 1);
                    setResults([...accumulated]);
                }
            }

            if (isMounted) setIsDone(true);
        }

        run();
        return () => { isMounted = false; };
    }, [allToDelete]);

    const counts = React.useMemo(() => ({
        deleted: results.filter(r => r.status === 'deleted').length,
        errors: results.filter(r => r.status === 'error').length,
    }), [results]);

    const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
    const barColor = isDone && counts.errors > 0 ? 'warning' : 'success';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 'var(--spacer-XS)' }}>
                    <span>{processed} / {total}</span>
                    <span>{percent}%</span>
                </div>
                <Progress value={percent} color={barColor} style={{ height: '8px' }} />
            </div>

            {isDone && (
                <div style={{
                    padding: 'var(--spacer-XS) var(--spacer-S)',
                    borderRadius: '6px',
                    background: wasCancelled ? 'var(--warning-bg)' : 'var(--success-bg)',
                    border: `var(--border-thin) solid ${wasCancelled ? 'var(--warning-border)' : 'var(--success-border)'}`,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: wasCancelled ? 'var(--warning-color)' : 'var(--success-color)',
                }}>
                    {wasCancelled ? t(TK.importClientsCancelled) : t(TK.clearDuplicatesDone)}
                </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--spacer-M)', flexWrap: 'wrap', fontSize: '0.9rem' }}>
                <span style={{ color: 'var(--success-color)' }}>
                    <strong>{counts.deleted}</strong> {t(TK.clearDuplicatesDeleted)}
                </span>
                {counts.errors > 0 && (
                    <span style={{ color: 'var(--danger-color)' }}>
                        <strong>{counts.errors}</strong> {t(TK.importClientsErrors)}
                    </span>
                )}
            </div>

            {results.filter(r => r.status === 'error').length > 0 && (
                <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: 'var(--border-thin) solid var(--danger-color)',
                    borderRadius: '6px',
                    padding: 'var(--spacer-S)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacer-XS)',
                }}>
                    {results
                        .filter((r): r is Extract<RowResult, { status: 'error' }> => r.status === 'error')
                        .map((r, i) => (
                            <div key={i} style={{ fontSize: '0.82rem' }}>
                                <strong>{r.name}</strong>:{' '}
                                <span style={{ color: 'var(--danger-color)' }}>{r.message}</span>
                            </div>
                        ))}
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacer-S)' }}>
                {!isDone && (
                    <button
                        onClick={() => { cancelRef.current = true; }}
                        style={{
                            padding: 'var(--spacer-XS) var(--spacer-S)',
                            borderRadius: '4px',
                            border: 'var(--border-thin) solid var(--appLigthGrey)',
                            background: 'white',
                            cursor: 'pointer',
                            color: 'var(--text-body)',
                            fontSize: '0.9rem',
                        }}
                    >
                        {t(TK.cancel)}
                    </button>
                )}
                {isDone && (
                    <button
                        onClick={onClose}
                        style={{
                            padding: 'var(--spacer-XS) var(--spacer-S)',
                            borderRadius: '4px',
                            border: 'none',
                            background: 'var(--appGreen)',
                            color: 'white',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                        }}
                    >
                        {t(TK.close)}
                    </button>
                )}
            </div>
        </div>
    );
};

export default ExecutionStep;
